import { type MouseEvent } from "react"
import { NextRouter } from "next/router"
import { type PersistentMessage } from "@/types/chat"
import { createAsyncThunk } from "@reduxjs/toolkit"
import nProgress from "nprogress"

import { isMicrositeHost } from "@/lib/is-embedded"
import { isChatPage } from "@/lib/utils/page"
import { sendAnalyticsEvent } from "../analytics/amplitude-utils"
import { userInitiatedSignOut } from "../auth/client"
import { selectEmbedded, selectEmbeddedGeoHint } from "./app-slice"
import { markChatTabEngagedFromState } from "./chat-tab-outside-nav"
import { createChat, goToChat } from "./chat-thunks"
import { closeDetailSheet, closeDetailSheetIfTrip } from "./detail-sheet-slice"
import { setView } from "./embedded-slice"
import { geoStopRequested } from "./geolocation-actions"
import { initialBotGreeting, sendMessage } from "./message-thunks"
import { AppDispatch, RootState } from "./redux-store"
import {
  selectNavSearchTerm,
  setBurgerOpen,
  setChatListDialogOpen,
  setNavSearchTerm,
  setNavValue,
} from "./site-nav-slice"
import userSlice from "./user-slice"

/** Sync mobile Chat-tab mode from loaded Redux state (e.g. route catch-up skips fetch). */
export const syncChatTabOutsideNavEngaged = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "siteNavThunks/syncChatTabOutsideNavEngaged",
  async (_, { dispatch, getState }) => {
    markChatTabEngagedFromState(dispatch, getState)
  }
)

export const handleChatMenuItemClick = createAsyncThunk<
  void,
  { e: MouseEvent; chatId: number; router: NextRouter },
  { dispatch: AppDispatch }
>(
  "siteNavThunks/handleChatMenuItemClick",
  async ({ e, chatId, router }, { dispatch }) => {
    void dispatch(closeDetailSheetIfTrip())
    dispatch(setNavValue(""))

    // if is chat page, override default link click behavior
    if (isChatPage(router)) {
      e.preventDefault()
      if (isMicrositeHost()) {
        // on microsite router.asPath reflects the browser url;
        // set the view so the embedded view also switches to chat
        dispatch(setView({ type: "chat" }))
      }
      await dispatch(
        goToChat({
          chatId,
          router,
        })
      )
    }

    sendAnalyticsEvent({
      name: "ChatSwitch",
      properties: { chat: { id: chatId } },
    })
  }
)

export const handleNewChat = createAsyncThunk<
  void,
  {
    router: NextRouter
    message?: PersistentMessage
    skipRouteCheck?: boolean
    queryParams?: URLSearchParams
  },
  {
    dispatch: AppDispatch
    state: RootState
  }
>(
  "siteNavThunks/handleNewChat",
  async (
    { router, message, skipRouteCheck, queryParams },
    { dispatch, getState }
  ) => {
    // if is chat page, override default link click behavior
    if (isChatPage(router) || skipRouteCheck) {
      if (isMicrositeHost()) {
        // same as handleChatMenuItemClick: router.asPath can be stale on microsite
        dispatch(setView({ type: "chat" }))
      }
      void dispatch(closeDetailSheet())
      nProgress.start()
      await dispatch(createChat({ message }))
      dispatch(userSlice.actions.patchUser({ has_chatted: true }))
      const embedded = selectEmbedded(getState())
      const geoHint = selectEmbeddedGeoHint(getState())

      if (embedded && geoHint && !message) {
        // embedded frame, either send initial user message from query param or generate a bot greeting
        const messageString = queryParams?.get("q")
        if (messageString) {
          await dispatch(
            sendMessage({ messageString, subType: "external-chat-cta" })
          )
        } else {
          await dispatch(initialBotGreeting())
        }
      }

      nProgress.done()
    } else {
      // runs initChatPage -> createChat, which fires ChatCreate with the chatId
      await router.push("/chat/new")
    }
    dispatch(setBurgerOpen(false))
  }
)

export const handleUserSignOut = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("siteNavThunks/handleUserSignOut", async (_, { dispatch }) => {
  dispatch(geoStopRequested("logout"))
  await userInitiatedSignOut()
})

export const createNewChatAndCloseNav = createAsyncThunk<
  void,
  NextRouter,
  { dispatch: AppDispatch }
>("siteNavThunks/createNewChatAndCloseNav", async (router, { dispatch }) => {
  dispatch(setNavValue(""))
  dispatch(setChatListDialogOpen(false))
  await dispatch(handleNewChat({ router }))
})

export const clearChatsSearch = createAsyncThunk<
  void,
  void,
  {
    dispatch: AppDispatch
    state: RootState
  }
>("siteNavThunks/clearChatsSearch", async (_, { dispatch, getState }) => {
  const state = getState()
  const navSearchTerm = selectNavSearchTerm(state)

  if (navSearchTerm) {
    dispatch(setNavSearchTerm(""))
  }
})
